/**
 * Os atestados desta oportunidade, em linhas compactas e expansíveis.
 *
 * Três fontes, todas reais, e cada uma diz o que é:
 *   · a PRÉ-ANÁLISE (`analise.atestados`): os atestados a submeter, com a
 *     prioridade que a leitura do edital atribuiu — só existe com o edital lido;
 *   · o CRUZAMENTO (`score.atestados`): os candidatos que o casador achou por
 *     palavras em comum — o que existe quando o edital não foi lido, e que NÃO
 *     é indicação;
 *   · a LEITURA INTEGRAL (`leitura.exigencias_cruzadas`): por exigência, o
 *     trecho do atestado que a cobre e o que falta.
 *
 * A linha de cima é o que se lê sem abrir: o identificador, a prioridade e o
 * título. Aberta, a linha separa o que o atestado COMPROVA (o trecho dele, a
 * relação com a exigência) do que ainda FALTA (a lacuna que a leitura apontou, e
 * o aviso quando ele não é atestado). O título e os dados do cliente vêm da
 * ficha do acervo, que a tela de Atestados já traz inteira (mesma chave de
 * cache: uma consulta só para as duas telas).
 *
 * Nada aqui altera score, recomendação nem cruzamento: só mostra.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ErroApi, type Ficha, type LicitacaoDetalhe } from "../api/cliente";
import { SeloNaoAtestado } from "./Selos";
import css from "./AtestadosDaOportunidade.module.css";

/** O código sem o `#`, para o caminho da URL — `/atestados/199`. */
const noCaminho = (codigo: string) => codigo.replace(/^#/, "");

const NATUREZA: Record<string, string> = {
  projeto:
    "Projeto executado, sem atestado no sistema: o documento tem de ser obtido com o cliente.",
  declaracao: "Declaração, e não atestado: não comprova capacidade técnica.",
  "nota fiscal": "Nota fiscal, e não atestado: não comprova capacidade técnica.",
};

type Linha = {
  codigo: string;
  prioridade: string;
  /** O que a pré-análise diz que ele comprova, e como se aplica. */
  comprova?: string | null;
  aplicacao?: string | null;
  natureza?: string | null;
  semelhanca?: number;
  /** Os radicais que o objeto e a ficha do atestado têm em comum: a prova do
   * cruzamento por palavras, a única que ele tem. */
  radicais?: string[];
  /** O nome que o cruzamento deu (entidade), quando não há ficha. */
  entidade?: string | null;
  /** O cruzamento pela ficha (0152): o que a IA julgou e os dois trechos que o
   * código conferiu palavra a palavra. */
  cobertura?: "total" | "parcial" | null;
  trechoOportunidade?: string | null;
  trechoAtestado?: string | null;
  porque?: string | null;
};

function LinhaDoAtestado({
  l,
  ficha,
  provas,
}: {
  l: Linha;
  ficha?: Ficha;
  provas: { n: number; citacao: string; cobertura: "total" | "parcial"; prova?: string | null; falta?: string | null; rebaixado?: string | null }[];
}) {
  const titulo = ficha?.titulo || l.entidade || l.comprova || "(sem título no acervo)";
  const comprovadas = provas.filter((p) => p.cobertura === "total");
  const parciais = provas.filter((p) => p.cobertura === "parcial");
  const pendencias = [
    ...parciais.filter((p) => p.falta).map((p) => `Exigência ${p.n}: ${p.falta}`),
    ...(l.natureza && NATUREZA[l.natureza] ? [NATUREZA[l.natureza]!] : []),
  ];
  return (
    <li className={css.linha}>
      <details>
        <summary className={css.resumo}>
          <b className={css.codigo}>{l.codigo}</b>
          <span className={css.prioridade}>{l.prioridade}</span>
          <span className={css.titulo}>{titulo}</span>
          {l.natureza && l.natureza !== "atestado" ? <SeloNaoAtestado natureza={l.natureza} /> : null}
        </summary>

        <div className={css.corpo}>
          {ficha?.atividade && (
            <>
              <h4>Descrição</h4>
              <p>{ficha.atividade}</p>
            </>
          )}

          <dl className={css.dados}>
            <div>
              <dt>Cliente</dt>
              <dd>{ficha?.orgao ?? "Não identificado"}{ficha?.uf ? ` · ${ficha.uf}` : ""}</dd>
            </div>
            <div>
              <dt>Data</dt>
              <dd>{ficha?.data ?? "Sem data"}</dd>
            </div>
            {ficha?.periodo && (
              <div>
                <dt>Período</dt>
                <dd>{ficha.periodo}</dd>
              </div>
            )}
            {ficha?.quantitativo && (
              <div>
                <dt>Quantitativo</dt>
                <dd>{ficha.quantitativo}</dd>
              </div>
            )}
            {ficha?.publico && (
              <div>
                <dt>Público</dt>
                <dd>{ficha.publico}</dd>
              </div>
            )}
            {l.semelhanca != null && (
              <div>
                <dt>Semelhança por palavras</dt>
                <dd>{l.semelhanca.toLocaleString("pt-BR", { minimumFractionDigits: 3 })}</dd>
              </div>
            )}
          </dl>

          {/* POR QUE O CRUZAMENTO PELA FICHA ESCOLHEU ESTE ATESTADO: a IA julgou
              o trabalho, e o código conferiu os dois trechos palavra a palavra. */}
          {l.cobertura && (
            <section aria-label="Por que a ficha escolheu este atestado">
              <h4>
                {l.cobertura === "total"
                  ? "O mesmo trabalho, na mesma área"
                  : "Trabalho próximo, cobre em parte"}
              </h4>
              {l.trechoOportunidade && (
                <p>
                  <span className={css.exigencia}>A oportunidade: </span>
                  <q>{l.trechoOportunidade}</q>
                </p>
              )}
              {l.trechoAtestado && (
                <p>
                  <span className={css.exigencia}>O atestado: </span>
                  <q>{l.trechoAtestado}</q>
                </p>
              )}
              {l.porque && <p className={css.apoio}>{l.porque}</p>}
            </section>
          )}

          {/* POR QUE O CRUZAMENTO TROUXE ESTE ATESTADO, sem IA: as palavras que
              o objeto e a ficha dele têm em comum. A pessoa confere, na
              descrição acima, se o TRABALHO é o mesmo — palavra em comum não é
              serviço em comum. */}
          {!l.cobertura && l.radicais && l.radicais.length > 0 && (
            <section aria-label="Palavras em comum com o objeto">
              <h4>Palavras em comum com o objeto</h4>
              <p>{l.radicais.join(" · ")}</p>
              <p className={css.apoio}>
                É a única prova do cruzamento por palavras. Confira na descrição acima se o
                trabalho executado é o que a oportunidade contrata.
              </p>
            </section>
          )}

          {(l.comprova || l.aplicacao) && (
            <>
              <h4>Relação com a solução</h4>
              {l.comprova && <p>{l.comprova}</p>}
              {l.aplicacao && <p className={css.apoio}>{l.aplicacao}</p>}
            </>
          )}

          {/* A EXPERIÊNCIA COMPROVADA: o trecho do atestado, conferido por
              código, para cada exigência que ele cobre por inteiro. */}
          <section className={css.comprovada} aria-label="Experiência comprovada">
            <h4>Experiência comprovada</h4>
            {comprovadas.length === 0 ? (
              <p className={css.apoio}>
                A leitura do edital não registra exigência coberta por inteiro por este atestado.
              </p>
            ) : (
              <ul>
                {comprovadas.map((p) => (
                  <li key={p.n}>
                    <span className={css.exigencia}>Exigência {p.n}: {p.citacao}</span>
                    <q>{p.prova ?? "trecho não gravado"}</q>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* A DOCUMENTAÇÃO PENDENTE: o que falta para cobrir por inteiro, e o
              aviso quando o item do acervo não é um atestado. */}
          <section className={css.pendente} aria-label="Pontos que precisam de complementação">
            <h4>Precisa de complementação</h4>
            {pendencias.length === 0 && parciais.length === 0 ? (
              <p className={css.apoio}>Nenhuma pendência apontada pela leitura.</p>
            ) : (
              <>
                {parciais.length > 0 && (
                  <ul>
                    {parciais.map((p) => (
                      <li key={p.n}>
                        <span className={css.exigencia}>Cobre em parte a exigência {p.n}: {p.citacao}</span>
                        {p.prova && <q>{p.prova}</q>}
                        {p.rebaixado && <span className={css.apoio}>{p.rebaixado}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {pendencias.length > 0 && (
                  <ul>
                    {pendencias.map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>

          <p className={css.rodape}>
            <Link to={`/atestados/${noCaminho(l.codigo)}`}>Abrir a ficha completa do atestado</Link>
            {ficha?.arquivo ? (
              <span className={css.arquivo}>Arquivo: {ficha.arquivo.split("/").pop()}</span>
            ) : (
              <span className={css.apoio}>Sem arquivo ligado a este atestado.</span>
            )}
          </p>
        </div>
      </details>
    </li>
  );
}

export function AtestadosDaOportunidade({ d }: { d: LicitacaoDetalhe }) {
  const analise = useQuery({
    queryKey: ["analise", d.id],
    queryFn: () => api.analiseDe(d.id),
    retry: false,
  });
  const score = useQuery({
    queryKey: ["score", d.id],
    queryFn: () => api.scoreDe(d.id),
    retry: false,
  });
  const leitura = useQuery({
    queryKey: ["leitura", d.id],
    queryFn: () => api.leituraDe(d.id),
    retry: false,
  });
  // O acervo inteiro, UMA vez: a mesma chave da tela de Atestados.
  const acervo = useQuery({
    queryKey: ["atestados", ""],
    queryFn: () => api.atestados({ busca: undefined, quantidade: 1000 }),
  });

  if (analise.isPending || score.isPending) {
    return <p className={css.apoio}>Carregando os atestados…</p>;
  }
  const quebrou = (q: { error: unknown }) =>
    q.error && !(q.error instanceof ErroApi && q.error.status === 404);
  if (quebrou(analise)) {
    return <p className={css.erro}>Não foi possível carregar a análise desta oportunidade.</p>;
  }

  const fichas = new Map((acervo.data?.itens ?? []).map((f) => [f.codigo, f]));
  const a = analise.data ?? null;
  const s = score.data ?? null;

  // As provas por atestado, a partir da leitura: cada exigência que o cita.
  const provasDe = new Map<string, Parameters<typeof LinhaDoAtestado>[0]["provas"]>();
  for (const e of leitura.data?.exigencias_cruzadas ?? []) {
    for (const p of e.atestados) {
      const lista = provasDe.get(p.atestado) ?? [];
      lista.push({ n: e.n, citacao: e.citacao, cobertura: p.cobertura, prova: p.prova, falta: p.falta, rebaixado: p.rebaixado });
      provasDe.set(p.atestado, lista);
    }
  }

  const linhas: Linha[] = [];
  const vistos = new Set<string>();
  if (a) {
    for (const x of a.atestados) {
      if (!x.codigo) continue;
      vistos.add(x.codigo);
      linhas.push({
        codigo: x.codigo,
        prioridade: x.prioridade ?? "Sem prioridade",
        comprova: x.comprova,
        aplicacao: x.aplicacao,
        natureza: x.natureza,
      });
    }
  }
  for (const x of s?.atestados ?? []) {
    if (vistos.has(x.codigo)) continue;
    vistos.add(x.codigo);
    const pelaFicha = s?.pela_ficha && x.cobertura;
    linhas.push({
      codigo: x.codigo,
      // Sem a leitura do edital são CANDIDATOS por palavras, e não indicações;
      // pela ficha, a IA diz se cobre por inteiro ou em parte.
      prioridade: pelaFicha
        ? x.cobertura === "total" ? "Pela ficha · por inteiro" : "Pela ficha · em parte"
        : a ? "Cruzamento" : "Candidato",
      entidade: x.entidade,
      natureza: x.natureza,
      // A semelhança da ficha é 1 ou 0,5 por construção: não é medida a mostrar.
      semelhanca: pelaFicha ? undefined : x.semelhanca,
      radicais: x.radicais,
      cobertura: pelaFicha ? x.cobertura : null,
      trechoOportunidade: x.trecho_oportunidade,
      trechoAtestado: x.trecho_atestado,
      porque: x.porque,
    });
  }
  // O que só a leitura integral trouxe (cobre alguma exigência e não está acima).
  for (const codigo of provasDe.keys()) {
    if (vistos.has(codigo)) continue;
    vistos.add(codigo);
    linhas.push({ codigo, prioridade: "Pela leitura" });
  }

  return (
    <div className={css.painel}>
      <p className={css.nota}>
        {a
          ? "Prioridade atribuída pela pré-análise, que leu o edital."
          : s?.pela_ficha
            ? "O edital ainda não foi lido inteiro: estes atestados foram escolhidos pelo cruzamento pela ficha — a IA comparou o objeto e o começo do trecho técnico com a ficha de cada atestado, e o código conferiu os dois trechos de cada par. É triagem do cruzamento: a prova completa vem da leitura do edital."
            : "O edital ainda não foi lido: estes são candidatos do cruzamento por palavras em comum — não são indicações, e a leitura do edital é quem separa uma coisa da outra."}
        {s?.cruzado_com != null && s.piso_radicais != null && (
          <>
            {" "}
            O objeto foi comparado com os {s.cruzado_com} itens do acervo; entram na lista os que
            têm pelo menos {s.piso_radicais} palavras em comum, e {s.atestados.length === 0
              ? "nenhum chegou lá"
              : `${s.atestados.length} chegaram`}.
          </>
        )}
      </p>
      {linhas.length === 0 ? (
        <p className={css.vazio}>
          {s?.pela_ficha
            ? "O cruzamento pela ficha comparou este objeto com os atestados da linha de atuação e do tipo de atividade, e nenhum mostra o mesmo trabalho. O que ele não achou, uma pessoa pode indicar na seção acima."
            : "Nenhum atestado do acervo alcançou o piso de radicais em comum com este objeto. Não é “nenhum parecido”: é “nada acima do piso de evidência” — e o que o casador não achou, uma pessoa pode indicar na seção acima."}
        </p>
      ) : (
        <ul className={css.lista} aria-label={`${linhas.length} atestados`}>
          {linhas.map((l) => (
            <LinhaDoAtestado key={l.codigo} l={l} ficha={fichas.get(l.codigo)} provas={provasDe.get(l.codigo) ?? []} />
          ))}
        </ul>
      )}
    </div>
  );
}
